import {
	Box,
	Button,
	Flex,
	Heading,
	IconButton,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	Textarea,
	VStack,
	Wrap,
	WrapItem,
	Tag,
	TagLabel,
	TagCloseButton,
} from "@chakra-ui/react";
import DOMPurify from "dompurify";
import htmlPrettify from "html-prettify";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import {
	useMailTemplate,
	useOrganisationNotificationSettings,
	useUpdateMailTemplate,
	useUpdateOrganisationNotificationTemplate,
	useSendTestNotification,
	useCreateNotificationTemplate,
} from "../../networking/organisation.hooks";
import { t } from "i18next";
import { NotificationTemplate } from "../../models/OrgNotificationSetting";
import AppInput from "../../components/Core/AppInput/AppInput";
import NotificationTemplateFilters, {
	TemplateCategory,
} from "../../components/Settings/NotificationTemplateFilters";
import { PdfTemplate, EditPdfTemplateModal } from "../../components/Settings/PdfTemplate";

const HandlebarsVariable = styled.span`
	background-color: #e2e8f0;
	color: #4a5568;
	padding: 2px 8px;
	border-radius: 12px;
	font-size: 0.875em;
	font-weight: 500;
	white-space: nowrap;
`;

const renderTextWithHandlebars = (text: string, pattern: RegExp = /{{[^}]+}}/g) => {
	const parts = text.split(new RegExp(`(${pattern.source})`, "g"));
	return parts.map((part, index) => {
		if (part.match(new RegExp(`^${pattern.source}$`))) {
			// Extract variable name by removing curly braces
			const varName = part.replace(/^\{+|\}+$/g, "");
			return <HandlebarsVariable key={index}>{varName}</HandlebarsVariable>;
		}
		return part;
	});
};

const addHandlebarsStylesToHTML = (html: string, pattern: RegExp = /{{[^}]+}}/g) => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(html, "text/html");

	const walkTextNodes = (node: Node) => {
		if (node.nodeType === Node.TEXT_NODE) {
			const text = node.textContent || "";
			if (pattern.test(text)) {
				const span = document.createElement("span");
				span.innerHTML = text.replace(pattern, (match) => {
					// Extract variable name by removing curly braces
					const varName = match.replace(/^\{+|\}+$/g, "");
					return `<span style="background-color: #e2e8f0; color: #4a5568; padding: 2px 8px; border-radius: 12px; font-size: 0.875em; font-weight: 500; white-space: nowrap;">${varName}</span>`;
				});
				node.parentNode?.replaceChild(span, node);
			}
		} else {
			for (let i = 0; i < node.childNodes.length; i++) {
				walkTextNodes(node.childNodes[i]);
			}
		}
	};

	walkTextNodes(doc.body);
	return doc.body.innerHTML;
};

const removeHandlebarsStylesFromHTML = (html: string) => {
	return html.replace(/<span style="[^"]*">({{[^}]+}})<\/span>/g, "$1");
};

const NotificationSettingsTab = () => {
	const { data } = useOrganisationNotificationSettings();
	const [editMailServiceTemplate, setEditMailServiceTemplate] = useState("");
	const [editTemplate, setEditTemplate] = useState<NotificationTemplate>();
	const [editPushTemplate, setEditPushTemplate] = useState<NotificationTemplate>();
	const [editSmsTemplate, setEditSmsTemplate] = useState<NotificationTemplate>();
	const [editPdfTemplate, setEditPdfTemplate] = useState<NotificationTemplate>();
	const [testTemplate, setTestTemplate] = useState<NotificationTemplate>();
	const [editDescriptionTemplate, setEditDescriptionTemplate] = useState<NotificationTemplate>();
	const [showCreateModal, setShowCreateModal] = useState(false);

	// Filtering state
	const [selectedCategory, setSelectedCategory] = useState<string>(
		t("notificationTags.allTemplates" as any) as string
	);
	const [searchQuery, setSearchQuery] = useState<string>("");

	// Query parameter detection for edit mode
	const location = useLocation();
	const isEditMode = new URLSearchParams(location.search).get("edit") === "1";

	// Extract unique categories from template tags
	const categories = useMemo<TemplateCategory[]>(() => {
		if (!data?.templates) return [];

		const tagCounts = new Map<string, number>();
		let uncategorizedCount = 0;

		data.templates.forEach((template) => {
			if (!template.tags || template.tags.length === 0) {
				uncategorizedCount++;
			} else {
				template.tags.forEach((tag) => {
					tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
				});
			}
		});

		const categoryList: TemplateCategory[] = [
			{
				name: t("notificationTags.allTemplates" as any) as string,
				count: data.templates.length,
			},
		];

		// Add Layouts category (count emailLayoutId + RECRUITMENT_FOOTER template)
		const recruitmentFooterExists = data.templates.some((t) => t.name === "RECRUITMENT_FOOTER");
		const layoutCount = (data?.settings.emailLayoutId ? 1 : 0) + (recruitmentFooterExists ? 1 : 0);

		if (layoutCount > 0) {
			categoryList.push({
				name: "Layouts",
				count: layoutCount,
				tag: "_layouts",
			});
		}

		// Add tag-based categories with translations
		Array.from(tagCounts.entries())
			.sort(([a], [b]) => a.localeCompare(b))
			.forEach(([tag, count]) => {
				categoryList.push({
					name: t(`notificationTags.${tag}` as any) as string,
					count,
					tag,
				});
			});

		// Add uncategorized if there are any
		if (uncategorizedCount > 0) {
			categoryList.push({
				name: t("notificationTags.uncategorized" as any) as string,
				count: uncategorizedCount,
			});
		}

		return categoryList;
	}, [data?.templates, data?.settings.emailLayoutId]);

	// Filter templates based on selected category and search query
	const filteredTemplates = useMemo(() => {
		if (!data?.templates) return [];

		// Find the selected category to get its tag key
		const selectedCategoryObj = categories.find((cat) => cat.name === selectedCategory);
		const allTemplatesName = t("notificationTags.allTemplates" as any) as string;
		const uncategorizedName = t("notificationTags.uncategorized" as any) as string;

		return data.templates.filter((template) => {
			// Category filter
			let categoryMatch = false;
			if (selectedCategory === allTemplatesName) {
				categoryMatch = true;
			} else if (selectedCategory === uncategorizedName) {
				categoryMatch = !template.tags || template.tags.length === 0;
			} else if (selectedCategory === "Layouts") {
				// Show RECRUITMENT_FOOTER template in Layouts category
				categoryMatch = template.name === "RECRUITMENT_FOOTER";
			} else if (selectedCategoryObj?.tag) {
				// Match by tag key
				categoryMatch = template.tags?.includes(selectedCategoryObj.tag) || false;
			}

			if (!categoryMatch) return false;

			// Search filter
			const searchMatch =
				searchQuery === "" ||
				template.humanReadableName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
				template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
				template.description?.toLowerCase().includes(searchQuery.toLowerCase());

			return searchMatch;
		});
	}, [data?.templates, selectedCategory, searchQuery, categories]);

	const handleClearFilters = () => {
		setSearchQuery("");
	};

	return (
		<>
			<Flex gap={0} alignItems="flex-start" flexDirection="column">
				<Box flex="1" w="full" maxW={{ md: "900px" }}>
					<NotificationTemplateFilters
						searchQuery={searchQuery}
						onSearchChange={setSearchQuery}
						onClearFilters={handleClearFilters}
						categories={categories}
						selectedCategory={selectedCategory}
						onCategorySelect={setSelectedCategory}
					/>

					{isEditMode && selectedCategory === t("notificationTags.recruitment" as any) && (
						<Box mb="4">
							<Button colorScheme="blue" size="sm" onClick={() => setShowCreateModal(true)}>
								Skapa ny rekryteringsmall
							</Button>
						</Box>
					)}

					<StyledSettings>
						{data?.settings.emailLayoutId &&
							(selectedCategory === (t("notificationTags.allTemplates" as any) as string) ||
								selectedCategory === "Layouts") && (
								<VStack alignItems={"flex-start"} mb="10" spacing={"4"}>
									<Heading fontSize={"md"}>{t("organisation.notifications.emailLayout")}</Heading>
									<Text>{t("organisation.notifications.emailLayoutDesc")}</Text>

									<MailTemplateFromMailService
										id={data.settings.emailLayoutId}
										onEdit={() => setEditMailServiceTemplate(data.settings.emailLayoutId!)}
									/>
								</VStack>
							)}
						{filteredTemplates.map((tpl) => (
							<VStack alignItems={"flex-start"} mb="10" spacing={"4"} key={tpl.name}>
								<Flex alignItems={"flex-start"} gap="2" w="full">
									<Box flex="1">
										<Heading fontSize={"lg"}>{tpl.humanReadableName || tpl.name}</Heading>
										{tpl.humanReadableName && tpl.humanReadableName !== tpl.name && (
											<Text fontSize={"2xs"} color="gray.500" mt="1">
												{tpl.name}
											</Text>
										)}
										{tpl.description && (
											<Text fontSize={"sm"} color="gray.600" mt="2">
												{tpl.description}
											</Text>
										)}
									</Box>
									<IconButton
										icon={<span>⋮</span>}
										size="sm"
										variant="ghost"
										aria-label="Redigera"
										onClick={() => setEditDescriptionTemplate(tpl)}
									/>
								</Flex>

								{tpl.channels.push && (
									<>
										<Heading fontSize={"sm"}>Pushnotis</Heading>
										<Box w="full" py="2" px="3" bg="white" fontFamily={"mono"} fontSize={"sm"}>
											<Flex justifyContent={"flex-end"} gap="2">
												<Button
													colorScheme="green"
													variant={"outline"}
													onClick={() => setTestTemplate(tpl)}
													size="xs"
													alignSelf={"flex-end"}
													mb="2"
												>
													{t("organisation.notifications.sendTest")}
												</Button>
												<Button
													colorScheme="blue"
													variant={"outline"}
													onClick={() => setEditPushTemplate(tpl)}
													size="xs"
													alignSelf={"flex-end"}
													mb="2"
												>
													{t("organisation.notifications.editTemplate")}
												</Button>
											</Flex>
											{tpl.pushTitle && (
												<Box mb="3" p="2" bg="gray.50" borderRadius="md">
													<Text fontSize="xs" color="gray.600" mb="1">
														Titel:
													</Text>
													<Text fontWeight="semibold">
														{renderTextWithHandlebars(tpl.pushTitle, /{[^}]+}/g)}
													</Text>
												</Box>
											)}
											<Box>{renderTextWithHandlebars(tpl.body || "", /{[^}]+}/g)}</Box>
										</Box>
									</>
								)}
								{tpl.channels.sms && (
									<>
										<Heading fontSize={"sm"}>SMS</Heading>
										<Box w="full" py="2" px="3" bg="white" fontFamily={"mono"} fontSize={"sm"}>
											<Flex justifyContent={"flex-end"} gap="2">
												<Button
													colorScheme="green"
													variant={"outline"}
													onClick={() => setTestTemplate(tpl)}
													size="xs"
													alignSelf={"flex-end"}
													mb="2"
												>
													{t("organisation.notifications.sendTest")}
												</Button>
												<Button
													colorScheme="blue"
													variant={"outline"}
													onClick={() => setEditSmsTemplate(tpl)}
													size="xs"
													alignSelf={"flex-end"}
													mb="2"
												>
													{t("organisation.notifications.editTemplate")}
												</Button>
											</Flex>
											<Box>{renderTextWithHandlebars(tpl.body || "", /{[^}]+}/g)}</Box>
										</Box>
									</>
								)}
								{tpl.channels.mail && tpl.emailTemplateId && (
									<>
										<Heading fontSize={"sm"}>E-post</Heading>
										<MailTemplateFromMailService
											id={tpl.emailTemplateId}
											onEdit={() => setEditMailServiceTemplate(tpl.emailTemplateId!)}
										/>
									</>
								)}
								{tpl.channels.mail && !tpl.emailTemplateId && (
									<>
										<Heading fontSize={"sm"}>E-post</Heading>
										<MailTemplate template={tpl} onEdit={() => setEditTemplate(tpl)} />
									</>
								)}
								{tpl.channels.pdf && (
									<>
										<Heading fontSize={"sm"}>PDF-mall</Heading>
										<PdfTemplate template={tpl} onEdit={() => setEditPdfTemplate(tpl)} />
									</>
								)}
							</VStack>
						))}
					</StyledSettings>
				</Box>
			</Flex>
			<EditMailServiceTemplateModal
				templateId={editMailServiceTemplate}
				onClose={() => setEditMailServiceTemplate("")}
			/>
			<EditMailTemplateModal
				key={editTemplate?.id}
				template={editTemplate}
				onClose={() => setEditTemplate(undefined)}
			/>
			<EditPushTemplateModal
				key={editPushTemplate?.id}
				template={editPushTemplate}
				onClose={() => setEditPushTemplate(undefined)}
			/>
			<EditSmsTemplateModal
				key={editSmsTemplate?.id}
				template={editSmsTemplate}
				onClose={() => setEditSmsTemplate(undefined)}
			/>
			<EditPdfTemplateModal
				key={editPdfTemplate?.id}
				template={editPdfTemplate}
				onClose={() => setEditPdfTemplate(undefined)}
			/>
			<SendTestNotificationModal
				key={testTemplate?.id}
				template={testTemplate}
				onClose={() => setTestTemplate(undefined)}
			/>
			<EditDescriptionModal
				key={editDescriptionTemplate?.id}
				template={editDescriptionTemplate}
				onClose={() => setEditDescriptionTemplate(undefined)}
			/>
			<CreateRecruitmentTemplateModal
				isOpen={showCreateModal}
				onClose={() => setShowCreateModal(false)}
			/>
		</>
	);
};

export default NotificationSettingsTab;

const StyledSettings = styled.div`
	max-width: 800px;

	section {
		margin: 0 0 2rem;
		border-bottom: 1px solid ${({ theme }) => theme.colors.COLOR_DIVIDER_GRAY};
		padding-bottom: 2rem;
	}
`;

const MailTemplateFromMailService: React.FC<{ id: string; onEdit: () => any }> = ({
	id,
	onEdit,
}) => {
	const { data, isLoading } = useMailTemplate(id);
	const [preview, setPreview] = useState(true);

	const { t } = useTranslation();

	const cleanHTML = useMemo(() => {
		if (data) {
			const prettified = htmlPrettify(data.html);
			const withHandlebars = addHandlebarsStylesToHTML(prettified);
			return DOMPurify.sanitize(withHandlebars);
		}

		return "";
	}, [data]);

	return (
		<Box w="full" py="2" px="3" bg="white" fontFamily={"mono"} fontSize={"sm"}>
			<Flex justifyContent={"flex-end"} gap="2">
				<Button
					colorScheme="blue"
					variant={"outline"}
					onClick={() => setPreview(!preview)}
					size="xs"
					alignSelf={"flex-end"}
					mb="2"
				>
					{preview ? "Visa HTML" : "Förhandsgranska"}
				</Button>
				<Button
					colorScheme="blue"
					variant={"outline"}
					onClick={onEdit}
					size="xs"
					alignSelf={"flex-end"}
					mb="2"
				>
					{t("organisation.notifications.editTemplate")}
				</Button>
			</Flex>
			{isLoading && <LoadingIndicator />}

			{data?.subject && (
				<Box mb="3" p="2" bg="gray.50" borderRadius="md">
					<Text fontSize="xs" color="gray.600" mb="1">
						Ämne:
					</Text>
					<Text fontWeight="semibold">{renderTextWithHandlebars(data.subject)}</Text>
				</Box>
			)}

			{preview ? (
				<MailTemplateStyles dangerouslySetInnerHTML={{ __html: cleanHTML }} />
			) : (
				<Box overflowX={"scroll"}>
					<pre>{data?.html}</pre>
				</Box>
			)}
		</Box>
	);
};

const MailTemplate: React.FC<{ template: NotificationTemplate; onEdit: () => any }> = ({
	template,
	onEdit,
}) => {
	return (
		<Box w="full" py="2" px="3" bg="white" fontFamily={"mono"} fontSize={"sm"}>
			<Flex justifyContent={"flex-end"} gap="2">
				<Button
					colorScheme="blue"
					variant={"outline"}
					onClick={onEdit}
					size="xs"
					alignSelf={"flex-end"}
					mb="2"
				>
					{t("organisation.notifications.editTemplate")}
				</Button>
			</Flex>
			{template.emailSubject && (
				<Box mb="3" p="2" bg="gray.50" borderRadius="md">
					<Text fontSize="xs" color="gray.600" mb="1">
						Ämne:
					</Text>
					<Text fontWeight="semibold">{renderTextWithHandlebars(template.emailSubject)}</Text>
				</Box>
			)}
			<Box overflowX={"scroll"}>
				<pre style={{ whiteSpace: "pre-wrap" }}>
					{renderTextWithHandlebars(template.body || "")}
				</pre>
			</Box>
		</Box>
	);
};

const MailTemplateStyles = styled.div`
	p {
		margin: 0 0 1rem 0;
	}

	li,
	ul,
	ol {
		list-style-position: inside;
	}
`;

const EditMailServiceTemplateModal: React.FC<{ templateId: string; onClose: () => any }> = ({
	templateId,
	onClose,
}) => {
	const { data } = useMailTemplate(templateId);
	const { mutate, isLoading } = useUpdateMailTemplate();
	const ref = useRef<HTMLTextAreaElement>(null);
	const [subject, setSubject] = useState("");

	const { t } = useTranslation();

	const cleanHTML = useMemo(() => {
		if (data) {
			const prettified = htmlPrettify(data.html);
			const withHandlebars = addHandlebarsStylesToHTML(prettified);
			return DOMPurify.sanitize(withHandlebars);
		}

		return "";
	}, [data]);

	const editableHTML = useMemo(() => {
		if (data) {
			return htmlPrettify(data.html);
		}

		return "";
	}, [data]);

	// Update subject state when data loads
	React.useEffect(() => {
		if (data?.subject) {
			setSubject(data.subject);
		}
	}, [data?.subject]);

	async function handleSubmit() {
		const prettyHtml = htmlPrettify(ref.current?.value || "");
		await mutate({ id: templateId, html: prettyHtml, subject });
		onClose();
	}

	return (
		<Modal isOpen={!!templateId} size="3xl" onClose={onClose}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>{data?.name}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<VStack spacing="3">
						<AppInput
							placeholder="organisation.notifications.emailSubject"
							value={subject}
							onChangeText={setSubject}
							containerMargin="0"
							margin="0"
						/>
						<Textarea
							h={"500px"}
							fontFamily={"mono"}
							fontSize={"sm"}
							defaultValue={editableHTML}
							ref={ref}
							margin="-0.4rem 0 0 0"
						/>
					</VStack>
				</ModalBody>

				<ModalFooter>
					<Button
						colorScheme="green"
						mr={3}
						onClick={handleSubmit}
						isLoading={isLoading}
						loadingText={t("LOADING") + "..."}
					>
						{t("SAVE")}
					</Button>
					<Button variant="ghost" onClick={onClose}>
						{t("CANCEL")}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

/**
 * User when editing a template from the organisation settings.
 * This is not be used when editing a mail template from the mail service.
 *
 * @param param0
 * @returns
 */
const EditMailTemplateModal: React.FC<{ template?: NotificationTemplate; onClose: () => any }> = ({
	template,
	onClose,
}) => {
	const { mutate, isLoading } = useUpdateOrganisationNotificationTemplate();
	const [body, setBody] = useState(template?.body || "");
	const [subject, setSubject] = useState(template?.emailSubject || "");
	const { t } = useTranslation();

	async function handleSubmit() {
		if (template) {
			await mutate({
				id: template.id,
				body: body,
				emailSubject: subject,
			});
		}
		onClose();
	}

	return (
		<Modal isOpen={!!template} size="3xl" onClose={onClose}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>{template?.humanReadableName || template?.name}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<VStack spacing="3">
						<AppInput value={subject} onChangeText={setSubject} containerMargin="0" margin="0" />
						<Textarea
							h={"500px"}
							fontFamily={"mono"}
							fontSize={"sm"}
							value={body}
							onChange={(e) => setBody(e.target.value)}
							margin="-0.4rem 0 0 0"
						/>
					</VStack>
				</ModalBody>

				<ModalFooter>
					<Button
						colorScheme="green"
						mr={3}
						onClick={handleSubmit}
						isLoading={isLoading}
						loadingText={t("LOADING") + "..."}
					>
						{t("SAVE")}
					</Button>
					<Button variant="ghost" onClick={onClose}>
						{t("CANCEL")}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

/**
 * Modal for editing push notification templates
 */
const EditPushTemplateModal: React.FC<{ template?: NotificationTemplate; onClose: () => any }> = ({
	template,
	onClose,
}) => {
	const { mutate, isLoading } = useUpdateOrganisationNotificationTemplate();
	const [body, setBody] = useState(template?.body || "");
	const [pushTitle, setPushTitle] = useState(template?.pushTitle || "");
	const { t } = useTranslation();

	async function handleSubmit() {
		if (template) {
			await mutate({
				id: template.id,
				body: body,
				pushTitle: pushTitle,
			});
		}
		onClose();
	}

	return (
		<Modal isOpen={!!template} size="3xl" onClose={onClose}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>{template?.name}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<VStack spacing="3">
						<AppInput
							placeholder="Push-notistitel"
							value={pushTitle}
							onChangeText={setPushTitle}
							containerMargin="0"
							margin="0"
						/>
						<Textarea
							h={"500px"}
							fontFamily={"mono"}
							fontSize={"sm"}
							placeholder="Meddelande"
							value={body}
							onChange={(e) => setBody(e.target.value)}
							margin="-0.4rem 0 0 0"
						/>
					</VStack>
				</ModalBody>

				<ModalFooter>
					<Button
						colorScheme="green"
						mr={3}
						onClick={handleSubmit}
						isLoading={isLoading}
						loadingText={t("LOADING") + "..."}
					>
						{t("SAVE")}
					</Button>
					<Button variant="ghost" onClick={onClose}>
						{t("CANCEL")}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

/**
 * Modal for editing SMS templates
 */
const EditSmsTemplateModal: React.FC<{ template?: NotificationTemplate; onClose: () => any }> = ({
	template,
	onClose,
}) => {
	const { mutate, isLoading } = useUpdateOrganisationNotificationTemplate();
	const [body, setBody] = useState(template?.body || "");
	const { t } = useTranslation();

	// Calculate SMS character count (rough estimate, 160 chars per SMS)
	const charCount = body.length;
	const smsCount = Math.ceil(charCount / 160);

	async function handleSubmit() {
		if (template) {
			await mutate({
				id: template.id,
				body: body,
			});
		}
		onClose();
	}

	return (
		<Modal isOpen={!!template} size="3xl" onClose={onClose}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>{template?.name}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<VStack spacing="3">
						<Textarea
							h={"300px"}
							fontFamily={"mono"}
							fontSize={"sm"}
							placeholder="SMS-meddelande"
							value={body}
							onChange={(e) => setBody(e.target.value)}
						/>
						<Text fontSize="sm" color="gray.600" alignSelf="flex-end">
							{charCount} tecken ({smsCount} SMS)
						</Text>
					</VStack>
				</ModalBody>

				<ModalFooter>
					<Button
						colorScheme="green"
						mr={3}
						onClick={handleSubmit}
						isLoading={isLoading}
						loadingText={t("LOADING") + "..."}
					>
						{t("SAVE")}
					</Button>
					<Button variant="ghost" onClick={onClose}>
						{t("CANCEL")}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

/**
 * Modal for sending test notifications
 */
const SendTestNotificationModal: React.FC<{
	template?: NotificationTemplate;
	onClose: () => any;
}> = ({ template, onClose }) => {
	const { mutate, isLoading } = useSendTestNotification();
	const [userEmail, setUserEmail] = useState("");
	const { t } = useTranslation();

	async function handleSubmit() {
		if (template) {
			await mutate({
				templateId: template.id,
				userEmail: userEmail || undefined,
			});
		}
		onClose();
	}

	const isValid = userEmail.trim() !== "";

	return (
		<Modal isOpen={!!template} size="xl" onClose={onClose}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>
					{t("organisation.notifications.sendTestModalTitle")} - {template?.name}
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<VStack spacing="4" alignItems="flex-start">
						<Text fontSize="sm" color="gray.600">
							{t("organisation.notifications.sendTestDescription")}
						</Text>
						<AppInput
							placeholder={t("organisation.notifications.userEmailPlaceholder")}
							value={userEmail}
							onChangeText={setUserEmail}
							containerMargin="0"
							margin="0"
						/>
						<Box mt="2">
							<Text fontSize="sm" fontWeight="bold" mb="1">
								{t("organisation.notifications.templatePreview")}
							</Text>
							{template?.pushTitle && (
								<Text fontSize="sm" color="gray.600">
									<strong>Title:</strong> {renderTextWithHandlebars(template.pushTitle)}
								</Text>
							)}
							{template?.body && (
								<Text fontSize="sm" color="gray.600">
									<strong>Body:</strong> {renderTextWithHandlebars(template.body)}
								</Text>
							)}
						</Box>
					</VStack>
				</ModalBody>

				<ModalFooter>
					<Button
						colorScheme="blue"
						mr={3}
						onClick={handleSubmit}
						isLoading={isLoading}
						loadingText={t("LOADING") + "..."}
						isDisabled={!isValid}
					>
						{t("organisation.notifications.sendTest")}
					</Button>
					<Button variant="ghost" onClick={onClose}>
						{t("CANCEL")}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

// Available tag options
const AVAILABLE_TAGS = [
	"recruitment",
	"onboarding",
	"timereports",
	"availability",
	"confirmations",
	"matching",
] as const;

/**
 * Modal for creating new recruitment templates
 */
const CreateRecruitmentTemplateModal: React.FC<{ isOpen: boolean; onClose: () => any }> = ({
	isOpen,
	onClose,
}) => {
	const { mutate, isLoading } = useCreateNotificationTemplate();
	const [name, setName] = useState("");
	const [humanReadableName, setHumanReadableName] = useState("");
	const [description, setDescription] = useState("");
	const { t } = useTranslation();

	// Reset form when modal opens
	React.useEffect(() => {
		if (isOpen) {
			setName("");
			setHumanReadableName("");
			setDescription("");
		}
	}, [isOpen]);

	async function handleSubmit() {
		await mutate({
			name: name.trim(),
			humanReadableName: humanReadableName.trim() || undefined,
			description: description.trim() || undefined,
		});
		onClose();
	}

	const isValid = name.trim() !== "";

	return (
		<Modal isOpen={isOpen} size="lg" onClose={onClose}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Skapa ny rekryteringsmall</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<VStack spacing="4" alignItems="stretch">
						<Box>
							<Text fontSize="sm" fontWeight="semibold" mb="2">
								Namn (nyckel) *
							</Text>
							<AppInput
								placeholder="T.ex. RECRUITMENT_WELCOME"
								value={name}
								onChangeText={setName}
								containerMargin="0"
								margin="0"
							/>
							<Text fontSize="xs" color="gray.600" mt="1">
								Systemnamn för mallen (krävs)
							</Text>
						</Box>
						<Box>
							<Text fontSize="sm" fontWeight="semibold" mb="2">
								Läsbar titel
							</Text>
							<AppInput
								placeholder="T.ex. Välkomstmeddelande till kandidat"
								value={humanReadableName}
								onChangeText={setHumanReadableName}
								containerMargin="0"
								margin="0"
							/>
							<Text fontSize="xs" color="gray.600" mt="1">
								Visas i gränssnittet (valfritt)
							</Text>
						</Box>
						<Box>
							<Text fontSize="sm" fontWeight="semibold" mb="2">
								Beskrivning
							</Text>
							<Textarea
								placeholder="Beskriv vad mallen används till"
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								rows={4}
							/>
							<Text fontSize="xs" color="gray.600" mt="1">
								Hjälptext för användare (valfritt)
							</Text>
						</Box>
						<Box bg="blue.50" p="3" borderRadius="md">
							<Text fontSize="xs" color="gray.700">
								<strong>Obs:</strong> Mallen kommer automatiskt att skapas med kategorin
								"recruitment" och kanalen "e-post".
							</Text>
						</Box>
					</VStack>
				</ModalBody>

				<ModalFooter>
					<Button
						colorScheme="green"
						mr={3}
						onClick={handleSubmit}
						isLoading={isLoading}
						loadingText={t("LOADING") + "..."}
						isDisabled={!isValid}
					>
						Skapa mall
					</Button>
					<Button variant="ghost" onClick={onClose}>
						{t("CANCEL")}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

/**
 * Modal for editing template description
 */
const EditDescriptionModal: React.FC<{ template?: NotificationTemplate; onClose: () => any }> = ({
	template,
	onClose,
}) => {
	const { mutate, isLoading } = useUpdateOrganisationNotificationTemplate();
	const [humanReadableName, setHumanReadableName] = useState(template?.humanReadableName || "");
	const [description, setDescription] = useState(template?.description || "");
	const [selectedTags, setSelectedTags] = useState<string[]>(template?.tags || []);
	const { t } = useTranslation();

	React.useEffect(() => {
		setHumanReadableName(template?.humanReadableName || "");
		setDescription(template?.description || "");
		setSelectedTags(template?.tags || []);
	}, [template]);

	const toggleTag = (tag: string) => {
		setSelectedTags((prev) =>
			prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
		);
	};

	async function handleSubmit() {
		if (template) {
			await mutate({
				id: template.id,
				humanReadableName: humanReadableName,
				description: description,
				tags: selectedTags,
			});
		}
		onClose();
	}

	return (
		<Modal isOpen={!!template} size="lg" onClose={onClose}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Redigera - {template?.name}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<VStack spacing="4" alignItems="stretch">
						<Box>
							<Text fontSize="sm" fontWeight="semibold" mb="2">
								Titel
							</Text>
							<AppInput
								placeholder="Ange en läsbar titel"
								value={humanReadableName}
								onChangeText={setHumanReadableName}
								containerMargin="0"
								margin="0"
							/>
						</Box>
						<Box>
							<Text fontSize="sm" fontWeight="semibold" mb="2">
								Beskrivning
							</Text>
							<Textarea
								placeholder="Ange en beskrivning (valfritt)"
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								rows={4}
							/>
						</Box>
						<Box>
							<Text fontSize="sm" fontWeight="semibold" mb="2">
								Kategorier (taggar)
							</Text>
							<Wrap spacing={2}>
								{AVAILABLE_TAGS.map((tag) => {
									const isSelected = selectedTags.includes(tag);
									return (
										<WrapItem key={tag}>
											<Tag
												size="md"
												variant={isSelected ? "solid" : "outline"}
												colorScheme={isSelected ? "blue" : "gray"}
												cursor="pointer"
												onClick={() => toggleTag(tag)}
											>
												<TagLabel>{t(`notificationTags.${tag}`)}</TagLabel>
												{isSelected && <TagCloseButton />}
											</Tag>
										</WrapItem>
									);
								})}
							</Wrap>
							<Text fontSize="xs" color="gray.600" mt="2">
								Välj en eller flera kategorier för att gruppera notisen
							</Text>
						</Box>
					</VStack>
				</ModalBody>

				<ModalFooter>
					<Button
						colorScheme="green"
						mr={3}
						onClick={handleSubmit}
						isLoading={isLoading}
						loadingText={t("LOADING") + "..."}
					>
						{t("SAVE")}
					</Button>
					<Button variant="ghost" onClick={onClose}>
						{t("CANCEL")}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
